import { FC } from 'react';
import { twJoin } from 'tailwind-merge';

import Button from 'molecules/Button';
import Image from 'molecules/Image';
import RichText from 'molecules/RichText';
import Stars from 'molecules/Stars';

import { TestimonialCardConfigProps } from 'organisms/Cards/Testimonial/config';
import {
  testimonialCardStyle,
  testimonialCompanyLogoStyle,
  testimonialHeadshotStyle,
  testimonialNameStyle,
} from 'organisms/Cards/Testimonial/styles';

import { getCleanDate } from 'utils/parse/parseDate';

interface TestimonialCardProps extends TestimonialCardConfigProps {
  current?: boolean;
  className?: string;
}

const TestimonialCard: FC<TestimonialCardProps> = ({
  quote,
  author,
  companyLogo,
  variation,
  rating,
  current,
  reviewDate,
  button,
  className,
}) => {
  if (variation === 'ratingCards') {
    const logoFile = author?.headshot?.file;

    return (
      <div className={twJoin(testimonialCardStyle({ variation }), className)}>
        <div className="flex flex-col items-start gap-7">
          {rating && rating > 0 && <Stars rating={rating} hideLabel />}
          <RichText text={quote} className="text-lg" />
        </div>
        <div className="flex flex-row items-start gap-5 self-stretch">
          {logoFile?.url && (
            <Image
              url={logoFile.url}
              alt={author?.headshot?.alt || 'G2 logo'}
              width={logoFile.width}
              height={logoFile.height}
              className="mt-1.5 size-8 shrink-0 object-cover"
            />
          )}
          <div className="flex flex-col">
            {author?.name && <div className="leading-6 text-gray-300">{author.name}</div>}
            {author?.title && <div className="leading-6 text-gray-300">{author.title}</div>}
            {reviewDate && <div className="leading-5 text-gray-300">{getCleanDate(reviewDate)}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={twJoin(testimonialCardStyle({ variation }), className)}>
      <div className="flex flex-col items-start gap-6 sm:gap-[26px]">
        {companyLogo?.file?.url && (
          <Image
            url={companyLogo.file.url}
            className={testimonialCompanyLogoStyle({ variation })}
            alt={companyLogo?.alt || 'company logo'}
            width={companyLogo.file.width}
            height={companyLogo.file.height}
            unsetMaxW
          />
        )}
        {variation === 'g2' && rating && rating > 0 && <Stars rating={rating} />}
        <RichText text={quote} className="text-lg" />
        {variation === 'g2' && button && <Button hierarchy="secondary" className="text-white" {...button} />}
      </div>
      <div className="flex w-full flex-row gap-3 sm:gap-6">
        {author && (
          <>
            {author.headshot?.file && (
              <Image
                url={author.headshot.file.url}
                alt={author.headshot?.alt || 'headshot'}
                width={author.headshot.file.width}
                height={author.headshot.file.height}
                className={testimonialHeadshotStyle({ variation })}
              />
            )}
            <div className="flex w-full flex-col justify-between gap-2 sm:flex-row ">
              <div className="flex flex-col justify-start gap-2">
                <div className={testimonialNameStyle({ isActive: !!current })}>{author?.name}</div>
                <div className="font-spaceGrotesk text-gray-300 text-overline-md">{author?.title}</div>
              </div>
              {variation === 'g2' && reviewDate && (
                <div className="text-lg font-bold text-gray-300">{getCleanDate(reviewDate)}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TestimonialCard;
